import axios from 'axios';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { jwtDecode } from "jwt-decode";

function useRequest(baseUrl) {
	const store = useStore();
	const { getAccessTokenSilently, user, logout } = useAuth0();

	const handleLogout = () =>
		logout({
			logoutParams: {
				returnTo: process.env.VUE_APP_BASE_URL,
			}
		});

	const makeRequest = async (endpoint, method, data = {}, params = {}, { rethrow = false } = {}) => {
		try {
			const token = await getAccessTokenSilently();

			if (!store.state.authentication.userIsAuthorized) {
				const decoded = jwtDecode(token);
				store.commit('SET_AUTHENTICATION', {
					...store.state.authentication,
					userIsAuthorized: true,
					roles: user.value['https://structure.io/roles'],
					permissions: decoded.permissions,
				});
			}

			const response = await axios({
				url: baseUrl + endpoint,
				method,
				data,
				params,
				headers: { Authorization: 'Bearer ' + token },
			});
			return response.data;
		} catch (error) {
			if (error.response && error.response.status === 401) {
				handleLogout();
			} else if (rethrow) {
				// Opt-out for callers that expect a failure and render it themselves — an
				// unprovisioned account, or a 409 the user has to confirm past.
				throw error;
			} else {
				error.message ? store.commit('SET_ERROR_MESSAGE', error.message) : store.commit('SET_ERROR_MESSAGE', 'An error occurred');
				store.commit('SET_SHOW_ERROR_MODAL', true);
				store.commit('SET_SHOW_LOADING_MODAL', false);
			}
		}
	};

	return { makeRequest };
}

export function useSCPRequest() {
	const { makeRequest } = useRequest(process.env.VUE_APP_SCP_BASE_URL);
	return { makeSCPRequest: makeRequest };
}

export function useCBERequest() {
	const { makeRequest } = useRequest(process.env.VUE_APP_CBE_BASE_URL);
	return { makeCBERequest: makeRequest };
}

// FastAPI puts a human-readable reason in `detail`. A 422 makes it a list of pydantic
// errors instead, which a template renders as unreadable JSON — so unpack it here, once,
// rather than in each caller that opted into `rethrow`.
export function reasonFor(error) {
	const detail = error.response?.data?.detail;
	if (Array.isArray(detail)) {
		return detail
			.map(item => `${item.loc?.slice(1).join('.') || 'field'}: ${item.msg}`)
			.join('; ');
	}
	return detail || error.message || 'Something went wrong.';
}

export const DOCUMENTATION_URL = 'https://docs.structure.io/ios-sdk/documentation/structure/';

export const MOBILE_BREAKPOINT = 912;

export function useDocumentationTracking() {
	const { isAuthenticated, user } = useAuth0();
	const { makeCBERequest } = useCBERequest();

	const handleDocumentationClick = async () => {
		if (!isAuthenticated.value) return false;

		// makeCBERequest swallows request errors internally and resolves to
		// undefined rather than rejecting, so success must be judged by its
		// return value, not by catching a thrown error.
		const response = await makeCBERequest(`/piccolo/users/onboardings/${user.value.sub}`, 'patch', { has_read_documentation: true });
		return response !== undefined;
	};

	return { documentationUrl: DOCUMENTATION_URL, handleDocumentationClick };
}
